import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import Header from "@/components/layout/header";
import Footer from "@/components/layout/footer";
import TherapistCard from "@/components/therapist-card";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { TherapistWithSpecialties, Availability } from "@shared/schema";
import { useAuth } from "@/hooks/use-auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export default function BookingPage() {
  const [location, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const query = new URLSearchParams(location.split("?")[1] || "");
  const preselectedTherapistId = query.get("therapistId");
  const preselectedDateStr = query.get("date");
  
  // Booking state
  const [selectedTherapistId, setSelectedTherapistId] = useState<number | null>(
    preselectedTherapistId ? parseInt(preselectedTherapistId) : null
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(
    preselectedDateStr ? new Date(preselectedDateStr) : undefined
  );
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    preselectedDateStr ? new Date(preselectedDateStr) : null
  );
  const [sessionType, setSessionType] = useState<"free" | "paid">("free");
  
  // Fetch therapists
  const { data: therapists, isLoading: therapistsLoading } = useQuery<TherapistWithSpecialties[]>({
    queryKey: ["/api/therapists"],
  });
  
  // Fetch selected therapist availabilities if a therapist is selected
  const { data: availabilities, isLoading: availabilitiesLoading } = useQuery<Availability[]>({
    queryKey: [`/api/therapists/${selectedTherapistId}/availabilities`],
    enabled: !!selectedTherapistId,
  });
  
  // Fetch free session status
  const { data: freeSessionStatus } = useQuery<{ freeSessionAvailable: boolean, resetDate: string }>({
    queryKey: ["/api/free-session-status"],
  });
  
  // Update session type based on availability
  useEffect(() => {
    if (freeSessionStatus && !freeSessionStatus.freeSessionAvailable) {
      setSessionType("paid");
    }
  }, [freeSessionStatus]);
  
  // Helper functions
  const getSelectedTherapist = () => {
    if (!selectedTherapistId || !therapists) return null;
    return therapists.find(t => t.id === selectedTherapistId) || null;
  };
  
  const getAvailableTimesForDate = (date: Date) => {
    if (!availabilities) return [];
    
    const selectedDateStr = date.toDateString();
    return availabilities
      .filter(a => new Date(a.date).toDateString() === selectedDateStr)
      .map(a => new Date(a.date))
      .sort((a, b) => a.getTime() - b.getTime());
  };
  
  const isDateAvailable = (date: Date) => {
    if (!availabilities) return false;
    
    return availabilities.some(a => 
      new Date(a.date).toDateString() === date.toDateString()
    );
  };
  
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };
  
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };
  
  const handleTimeSelect = (time: Date) => {
    setSelectedTime(time);
  };
  
  const handleConfirmBooking = async () => {
    if (!selectedTherapistId || !selectedTime || !user) return;
    
    if (sessionType === "free") {
      try {
        // Criar a sessão gratuita diretamente
        await apiRequest("POST", "/api/sessions", {
          therapistId: selectedTherapistId,
          date: selectedTime.toISOString(),
          isFree: true,
          status: "scheduled",
        });
        
        // Invalidar a consulta de sessões para atualizar a lista
        queryClient.invalidateQueries({ queryKey: ["/api/sessions"] });
        queryClient.invalidateQueries({ queryKey: ["/api/free-session-status"] });
        
        // Mostrar mensagem de sucesso e redirecionar
        toast({
          title: "Sessão agendada com sucesso",
          description: "Sua sessão gratuita foi agendada.",
        });
        
        navigate("/sessions");
      } catch (error: any) {
        toast({
          title: "Erro ao agendar sessão",
          description: error.message || "Não foi possível agendar a sessão gratuita.",
          variant: "destructive",
        });
      }
    } else {
      // Paid session - redirect to checkout
      navigate(`/checkout/${selectedTherapistId}/${selectedTime.toISOString()}`);
    }
  };
  
  const availableTimesForSelectedDate = selectedDate ? getAvailableTimesForDate(selectedDate) : [];
  const selectedTherapist = getSelectedTherapist();
  
  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-grow bg-neutral">
        <section className="container mx-auto px-4 py-8 md:py-12">
          <Card className="bg-white shadow-sm p-6 md:p-8">
            <h2 className="text-xl md:text-2xl font-heading font-bold mb-6">Agendar Sessão</h2>
            
            {/* Therapist Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">1. Selecione um terapeuta</h3>
              {therapistsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {therapists?.map(therapist => (
                    <div 
                      key={therapist.id}
                      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                        selectedTherapistId === therapist.id 
                          ? "border-primary bg-primary/5" 
                          : "border-gray-200 hover:border-primary"
                      }`}
                      onClick={() => setSelectedTherapistId(therapist.id)}
                    >
                      <div className="flex items-center">
                        <img 
                          src={therapist.image_url}
                          alt={therapist.name} 
                          className="w-12 h-12 object-cover rounded-full mr-3"
                        />
                        <div>
                          <h4 className="font-medium">{therapist.name}</h4>
                          <p className="text-sm text-gray-600">{therapist.title}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Date Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">2. Escolha uma data</h3>
              {!selectedTherapistId ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                  Selecione um terapeuta primeiro
                </div>
              ) : availabilitiesLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-4 md:p-6">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      // Disable dates in the past
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);
                      const isPast = date < yesterday;
                      
                      // Disable dates with no availability
                      const hasAvailability = isDateAvailable(date);
                      
                      return isPast || !hasAvailability;
                    }}
                    className="bg-white rounded-lg shadow-sm"
                  />
                </div>
              )}
            </div>
            
            {/* Time Selection */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">
                3. Escolha um horário
                {selectedDate && ` para ${selectedDate.toLocaleDateString('pt-BR')}`}
              </h3>
              
              {!selectedDate ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                  Selecione uma data primeiro
                </div>
              ) : availableTimesForSelectedDate.length === 0 ? (
                <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
                  Não há horários disponíveis para esta data
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {availableTimesForSelectedDate.map((time, index) => (
                    <button
                      key={index}
                      className={`border rounded-md p-2 text-center transition-colors ${
                        selectedTime && selectedTime.getTime() === time.getTime()
                          ? "border-primary bg-primary/5 font-medium"
                          : "border-gray-200 hover:border-primary cursor-pointer"
                      }`}
                      onClick={() => handleTimeSelect(time)}
                    >
                      {formatTime(time)}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Session Type */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-4">4. Tipo de sessão</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div 
                  className={`border rounded-lg p-4 cursor-pointer ${
                    !freeSessionStatus?.freeSessionAvailable
                      ? "border-gray-200 bg-gray-50 opacity-50 cursor-not-allowed"
                      : sessionType === "free"
                        ? "border-primary bg-primary/5"
                        : "border-gray-200 hover:border-primary"
                  }`}
                  onClick={() => freeSessionStatus?.freeSessionAvailable && setSessionType("free")}
                >
                  <div className="flex items-start">
                    <div className={`rounded-full p-2 mr-3 ${
                      freeSessionStatus?.freeSessionAvailable ? "bg-primary/20" : "bg-gray-100"
                    }`}>
                      <i className={`fas fa-ticket-alt ${
                        freeSessionStatus?.freeSessionAvailable ? "text-primary" : "text-gray-400"
                      }`}></i>
                    </div>
                    <div>
                      <h4 className="font-medium">Sessão Mensal Gratuita</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {freeSessionStatus?.freeSessionAvailable 
                          ? "Use sua sessão gratuita mensal."
                          : "Você já utilizou sua sessão gratuita deste mês."}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div 
                  className={`border rounded-lg p-4 cursor-pointer ${
                    sessionType === "paid"
                      ? "border-primary bg-primary/5"
                      : "border-gray-200 hover:border-primary"
                  }`}
                  onClick={() => setSessionType("paid")}
                >
                  <div className="flex items-start">
                    <div className={`rounded-full p-2 mr-3 ${
                      sessionType === "paid" ? "bg-primary/20" : "bg-gray-100"
                    }`}>
                      <i className={`fas fa-credit-card ${
                        sessionType === "paid" ? "text-primary" : "text-gray-600"
                      }`}></i>
                    </div>
                    <div>
                      <h4 className="font-medium">Sessão Adicional</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {selectedTherapist 
                          ? `${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(selectedTherapist.hourly_rate / 100)} por sessão com este profissional.`
                          : "Selecione um terapeuta para ver o valor."}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end mt-8">
              <Button
                onClick={handleConfirmBooking}
                disabled={!selectedTherapistId || !selectedTime}
                className="bg-primary hover:bg-secondary text-white rounded-full py-2 px-6 font-medium"
              >
                Confirmar Agendamento
              </Button>
            </div>
          </Card>
        </section>
      </main>
      
      <Footer />
    </div>
  );
}
